import { useState } from 'react';
import { useSources, useDeleteSource, useTestSource, type DataSourceResponse } from '@/hooks/useSources';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { formatDate } from '@/lib/utils';
import { SourceModal } from '@/components/modals/SourceModal';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useToast } from '@/hooks/useToast';
import { SkeletonCardGrid } from '@/components/skeletons/SkeletonLoaders';
import { EmptySourcesState, ErrorState } from '@/components/EmptyState';

interface SourcesData {
  items: DataSourceResponse[];
  total: number;
  pages: number;
  page: number;
  limit: number;
}

export const Sources = () => {
  const [page, setPage] = useState(1);
  const [sourceModalOpen, setSourceModalOpen] = useState(false);
  const [selectedSource, setSelectedSource] = useState<DataSourceResponse | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [sourceToDelete, setSourceToDelete] = useState<string | null>(null);

  const { data, isLoading, error, refetch } = useSources({ page, limit: 20 });
  const deleteSource = useDeleteSource();
  const testSource = useTestSource();
  const { toast } = useToast();

  const typedData = data as SourcesData | undefined;

  const handleAddSource = () => {
    setSelectedSource(null);
    setSourceModalOpen(true);
  };

  const handleEditSource = (source: DataSourceResponse) => {
    setSelectedSource(source);
    setSourceModalOpen(true);
  };

  const handleDeleteClick = (id: string) => {
    setSourceToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!sourceToDelete) return;

    try {
      await deleteSource.mutateAsync(sourceToDelete);
      toast({
        title: 'Success',
        description: 'Data source deleted successfully',
      });
      setDeleteDialogOpen(false);
      setSourceToDelete(null);
    } catch (err: unknown) {
      const error = err as Error;
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete data source',
        variant: 'destructive',
      });
    }
  };

  const handleTest = async (id: string) => {
    try {
      const result = await testSource.mutateAsync(id);
      // Backend returns connection_successful, not success
      const success = (result as { connection_successful?: boolean }).connection_successful;
      const message = (result as { message?: string }).message;

      toast({
        title: success ? 'Success' : 'Failed',
        description: message || (success
          ? 'Connection test successful!'
          : 'Connection test failed'),
        variant: success ? 'default' : 'destructive',
      });
    } catch (err: unknown) {
      const error = err as Error;
      toast({
        title: 'Error',
        description: error.message || 'Connection test failed',
        variant: 'destructive',
      });
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Data Sources</h1>
            <p className="text-muted-foreground">
              Manage your connected data sources
            </p>
          </div>
          <Button disabled>Add Source</Button>
        </div>
        <SkeletonCardGrid count={6} />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Data Sources</h1>
            <p className="text-muted-foreground">
              Manage your connected data sources
            </p>
          </div>
          <Button onClick={handleAddSource}>Add Source</Button>
        </div>
        <ErrorState
          message={error instanceof Error ? error.message : 'Failed to load data sources'}
          onRetry={() => refetch()}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Data Sources</h1>
          <p className="text-muted-foreground">
            Manage your connected data sources
          </p>
        </div>
        <Button onClick={handleAddSource}>Add Source</Button>
      </div>

      {/* Empty State */}
      {(!typedData?.items || typedData.items.length === 0) ? (
        <EmptySourcesState onAdd={handleAddSource} />
      ) : (
        <>
          {/* Sources Grid */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {typedData.items.map((source) => (
              <Card key={source.source_id}>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span className="truncate">{source.name}</span>
                    <span
                      className={`text-xs px-2 py-1 rounded whitespace-nowrap ml-2 ${
                        source.is_active
                          ? 'bg-success/10 text-success'
                          : 'bg-muted text-muted-foreground'
                      }`}
                    >
                      {source.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </CardTitle>
                  <CardDescription>{source.source_type || 'N/A'}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {source.description && (
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {source.description}
                      </p>
                    )}
                    <div className="text-sm">
                      <div className="text-muted-foreground">Created</div>
                      <div>{formatDate(source.created_at)}</div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleTest(source.source_id)}
                        disabled={testSource.isPending}
                      >
                        {testSource.isPending ? 'Testing...' : 'Test'}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEditSource(source)}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDeleteClick(source.source_id)}
                        disabled={deleteSource.isPending}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Pagination */}
          {typedData.total > 20 && (
            <div className="flex items-center justify-center gap-2">
              <Button
                variant="outline"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Previous
              </Button>
              <span className="text-sm text-muted-foreground">
                Page {page} of {typedData.pages}
              </span>
              <Button
                variant="outline"
                onClick={() => setPage((p) => p + 1)}
                disabled={page >= typedData.pages}
              >
                Next
              </Button>
            </div>
          )}
        </>
      )}

      {/* Modals */}
      <SourceModal
        open={sourceModalOpen}
        onOpenChange={setSourceModalOpen}
        source={selectedSource ?? undefined}
      />

      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete Data Source"
        description="Are you sure you want to delete this data source? This action cannot be undone."
        onConfirm={handleDeleteConfirm}
        confirmText="Delete"
        variant="destructive"
        loading={deleteSource.isPending}
      />
    </div>
  );
};
